import React, { useEffect, useState, useRef, useCallback } from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Container, 
  Tabs,
  Tab,
  LinearProgress,
  alpha,
  Fade,
  useTheme
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { MusicNote } from "@mui/icons-material";
import axiosInstance from "../api/axiosConfig";

interface VideoItem {
  vid: string;
  title: string;
  filesize?: number;
  duration?: number;
  resolution?: string | null;
}

interface VideoListProps {
  searchResults?: VideoItem[];
  mode?: 'default' | 'random';
}

const MODE_KEY = "videoListMode";

const VideoList: React.FC<VideoListProps> = ({ searchResults, mode: modeProp }) => {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progressMap, setProgressMap] = useState<Record<string, number>>({});
  const [seed, setSeed] = useState<string>(() => Math.random().toString(36).slice(2, 10));
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  
  const pageRef = useRef<number>(page);
  const hasMoreRef = useRef<boolean>(hasMore);
  const isLoadingRef = useRef<boolean>(isLoading);
  const videosRef = useRef<VideoItem[]>([]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { vid: currentVid } = useParams<{ vid: string }>();
  
  // Resolve mode: prop > localStorage > default
  const resolvedMode = modeProp || (localStorage.getItem(MODE_KEY) as 'default' | 'random' | null) || 'default';
  const [activeMode, setActiveMode] = useState<string>(resolvedMode);

  // Persist mode to localStorage when changed
  const handleModeChange = (_: React.SyntheticEvent, newMode: 'default' | 'random') => {
    setActiveMode(newMode);
    localStorage.setItem(MODE_KEY, newMode);
  };

  // Generate new seed when mode changes
  useEffect(() => {
    setSeed(Math.random().toString(36).slice(2, 10));
  }, [activeMode]);

  const resetState = useCallback(() => {
    pageRef.current = 1;
    hasMoreRef.current = true;
    videosRef.current = [];
    setPage(1);
    setHasMore(true);
    setVideos([]);
  }, []);

  const fetchPage = useCallback(
    async (reset = false) => {
      if (isLoadingRef.current || (!hasMoreRef.current && !reset)) return;
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        if (activeMode === 'random') {
          // Random mode: use seeded API
          const toFetch = reset ? 1 : pageRef.current;
          const resp = await axiosInstance.get(
            `${backendUrl}/api/videos/random-seeded?seed=${seed}&page=${toFetch}&limit=12`
          );
          const newVideos: VideoItem[] = resp.data.videos || [];
          const mergedVideos = reset ? newVideos : [...videosRef.current, ...newVideos];
          setVideos(mergedVideos);
          videosRef.current = mergedVideos;

          const nextPage = reset ? 2 : pageRef.current + 1;
          pageRef.current = nextPage;
          setPage(nextPage);

          // Check if we've exhausted the list
          const totalVideos = resp.data.totalVideos || 0;
          const totalPages = totalVideos > 0 ? Math.ceil(totalVideos / 12) : 0;
          const more = newVideos.length > 0 && toFetch < totalPages;
          hasMoreRef.current = more;
          setHasMore(more);
        } else {
          // Default mode: use standard paginated list
          const toFetch = reset ? 1 : pageRef.current;
          const resp = await axiosInstance.get(`${backendUrl}/api/videos?page=${toFetch}&limit=12`);
          const newVideos: VideoItem[] = resp.data.videos || [];
          const mergedVideos = reset ? newVideos : [...videosRef.current, ...newVideos];
          setVideos(mergedVideos);
          videosRef.current = mergedVideos;

          const nextPage = reset ? 2 : pageRef.current + 1;
          pageRef.current = nextPage;
          setPage(nextPage);

          const more = newVideos.length > 0 && (reset ? 1 : toFetch) < (resp.data.totalPages || 1);
          hasMoreRef.current = more;
          setHasMore(more);
        }
      } catch (err) {
        console.error("Error loading videos:", err);
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [backendUrl, activeMode, seed]
  );

  // Reset and fetch when mode changes
  useEffect(() => {
    resetState();
    fetchPage(true);
  }, [activeMode, resetState, fetchPage]);

  // Initial load
  useEffect(() => {
    fetchPage(true);
    const handler = () => {
      resetState();
      fetchPage(true);
    };
    window.addEventListener("miniVideoSubmitted", handler as EventListener);
    return () => window.removeEventListener("miniVideoSubmitted", handler as EventListener);
  }, [fetchPage, resetState]);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (!entries || entries.length === 0) return;
      if (entries[0].isIntersecting && hasMoreRef.current) {
        fetchPage();
      }
    });
    if (sentinelRef.current) observer.current.observe(sentinelRef.current);
    return () => observer.current?.disconnect();
  }, [fetchPage, hasMore]);

  // Polling logic for processing videos
  useEffect(() => {
    let mounted = true;
    const fetchProgress = async () => {
      const vidsToPoll = videos.filter(v => v.filesize == null).map(v => v.vid);
      if (vidsToPoll.length === 0) return;
      try {
        const results = await Promise.all(vidsToPoll.map(async (vid) => {
          try {
            const resp = await axiosInstance.get(`${backendUrl}/api/progress/${vid}`);
            return { vid, progress: resp.data.progress || 0 };
          } catch (e) {
            return { vid, progress: 0 };
          }
        }));
        if (!mounted) return;

        setProgressMap((prev) => {
          const copy = { ...prev };
          results.forEach(r => { if (typeof r.progress === 'number') copy[r.vid] = r.progress; });
          return copy;
        });

        const completed = results.filter(r => typeof r.progress === 'number' && r.progress >= 100).map(r => r.vid);
        if (completed.length > 0) {
          try {
            const statsResults = await Promise.all(completed.map(async (id) => {
              try {
                const resp = await axiosInstance.get(`${backendUrl}/api/stats/${id}`);
                return { id, stats: resp.data };
              } catch (e) {
                return { id, stats: null };
              }
            }));

            setVideos((prev) => {
              const map: Record<string, any> = {};
              statsResults.forEach(r => { if (r.stats) map[r.id] = r.stats; });
              if (Object.keys(map).length === 0) return prev;
              return prev.map(v => {
                const s = map[v.vid];
                if (!s) return v;
                return { ...v, ...s } as typeof v;
              });
            });

            setProgressMap((prev) => {
              const copy = { ...prev };
              completed.forEach(id => { copy[id] = 100; });
              return copy;
            });
          } catch (e) {}
        }
      } catch (e) {}
    };

    fetchProgress();
    const id = setInterval(fetchProgress, 2000);
    return () => { mounted = false; clearInterval(id); };
  }, [videos, backendUrl]);

  const formatSeconds = (seconds?: number) => {
    if (!seconds) return "N/A";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const pad = (n: number) => String(n).padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  const formatFilesize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return "⏳";
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const handleNavigate = (vid: string) => {
    const path = location.pathname || "";
    const isMusicPage = path.startsWith("/music") || path.includes("/music/");
    const prefix = isMusicPage ? "/music" : "/video";
    navigate(`${prefix}/${vid}`);
    if (!isMusicPage && window.innerWidth > 600) {
      setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: "smooth" }), 50);
    }
  };

  const isCurrentlyPlaying = (vid: string) => currentVid === vid;
  const isSearchMode = searchResults && searchResults.length > 0;
  const displayVideos = isSearchMode ? searchResults : videos;

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: 1,
        mb: 4,
        px: { xs: 0, sm: 2 }, // Add padding on larger screens
      }}
    >
      {isSearchMode && (
        <Typography variant="subtitle2" sx={{ mb: 2, px: 1, color: 'text.secondary', fontWeight: 600 }}>
          SEARCH RESULTS ({searchResults.length})
        </Typography>
      )}
      
      {!isSearchMode && (
        <Box sx={{ mb: 1.5, px: 1 }}>
          <Tabs
            value={activeMode === 'random' ? 'random' : 'default'}
            onChange={handleModeChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.4),
              borderRadius: 2,
              '& .MuiTab-root': {
                typography: { fontWeight: 600 },
              },
            }}
          >
            <Tab label="Default" value="default" />
            <Tab label="Random" value="random" />
          </Tabs>
        </Box>
      )}
      
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {displayVideos.map((v) => (
          <Fade in={true} key={v.vid}>
            <Card
              onClick={() => handleNavigate(v.vid)}
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                cursor: "pointer",
                bgcolor: isCurrentlyPlaying(v.vid) 
                  ? alpha('#1db954', 0.08) 
                  : alpha(theme.palette.background.paper, 0.1),
                border: '1px solid',
                borderColor: isCurrentlyPlaying(v.vid) 
                  ? '#1db954' 
                  : 'rgba(255,255,255,0.05)',
                borderRadius: 2,
                overflow: 'visible', // Allow hover effects to pop
                transition: 'all 0.2s ease',
                // backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  borderColor: 'rgba(255,255,255,0.15)',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: { xs: 120, sm: 160 },
                  height: { xs: 68, sm: 90 },
                  borderRadius: 2,
                  overflow: "hidden",
                  flexShrink: 0,
                  bgcolor: 'black'
                }}
              >
                <Box
                  component="img"
                  src={`${backendUrl}/api/thumbnail/${v.vid}`}
                  alt={v.title}
                  onError={(e: any) => { e.currentTarget.onerror = null; e.currentTarget.src = '/404notfound.jpg'; }}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: isCurrentlyPlaying(v.vid) ? 0.6 : 1
                  }}
                />

                {isCurrentlyPlaying(v.vid) && (
                  <Box sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                    <MusicNote sx={{ color: '#1db954', fontSize: 32, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                  </Box>
                )}

                {v.duration !== undefined && (
                  <Box sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    bgcolor: "rgba(0,0,0,0.8)",
                    color: "white",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: 10,
                    fontWeight: 'bold',
                    backdropFilter: 'blur(2px)'
                  }}>
                    {formatSeconds(v.duration)}
                  </Box>
                )}
                
                {progressMap[v.vid] > 0 && progressMap[v.vid] < 100 && (
                   <LinearProgress variant="determinate" value={progressMap[v.vid]} sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3 }} />
                )}
              </Box>

              <CardContent sx={{ flex: 1, py: 1, px: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography 
                  variant="subtitle1" 
                  component="div"
                  sx={{
                    fontWeight: isCurrentlyPlaying(v.vid) ? 700 : 500,
                    color: isCurrentlyPlaying(v.vid) ? '#1db954' : 'text.primary',
                    lineHeight: 1.2,
                    mb: 0.5,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden'
                  }}
                >
                  {v.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{formatFilesize(v.filesize)}</span>
                  <span>•</span>
                  <span>{v.resolution || 'Processing...'}</span>
                </Typography>
              </CardContent>
            </Card>
          </Fade>
        ))}

        {!isSearchMode && <div ref={sentinelRef} style={{ height: 20 }} />}
      </Box>
      
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
           <LinearProgress sx={{ width: '100%', maxWidth: 200, borderRadius: 1 }} />
        </Box>
      )}

      {!isSearchMode && !hasMore && (
        <Typography variant="caption" align="center" display="block" sx={{ mt: 4, color: 'text.disabled' }}>
          • END OF LIST •
        </Typography>
      )}
    </Container>
  );
};

export default VideoList;
